import { FilterValue } from '@/models';
import { Filter } from '@cubejs-client/core';

const normalizeToStringArray = (filterValue?: FilterValue): string[] => {
  if (typeof filterValue === 'string') return [filterValue];

  if (Array.isArray(filterValue)) {
    return filterValue.filter((filterItem): filterItem is string => typeof filterItem === 'string');
  }
  return [];
};

const getNumberRange = (rangeStart: number, rangeEnd: number): number[] => {
  if (!Number.isInteger(rangeStart) || !Number.isInteger(rangeEnd) || rangeStart > rangeEnd) return [];
  return Array.from({ length: rangeEnd - rangeStart + 1 }, (_, index) => rangeStart + index);
};

const getRangeValuesFromString = (rangeString: string): string[] => {
  if (!rangeString.includes('-')) {
    const singleValue = Number(rangeString);
    return Number.isInteger(singleValue) ? [String(singleValue)] : [];
  }

  const [rangeStart, rangeEnd] = rangeString.split('-').map(rangePart => Number(rangePart.trim()));

  if (!Number.isInteger(rangeStart) || !Number.isInteger(rangeEnd)) return [];

  return getNumberRange(rangeStart, rangeEnd).map(String);
};

export const buildRangeMultiselectFilter = (
  memberName: string,
  filterValue: FilterValue,
  noDataValue: string,
): Filter | undefined => {
  const selectedValues = normalizeToStringArray(filterValue);

  if (!selectedValues.length) {
    return undefined;
  }

  const includesNoData = selectedValues.includes(noDataValue);

  const numericValues = selectedValues
    .filter(selectedValue => selectedValue !== noDataValue)
    .flatMap(getRangeValuesFromString);

  const uniqueNumericValues = Array.from(new Set(numericValues));

  const numericFilter: Filter | undefined =
    uniqueNumericValues.length > 0
      ? {
          member: memberName,
          operator: 'equals',
          values: uniqueNumericValues,
        }
      : undefined;

  if (!includesNoData) return numericFilter;

  const noDataFilter: Filter = {
    member: memberName,
    operator: 'notSet',
  };

  return numericFilter ? { or: [numericFilter, noDataFilter] } : noDataFilter;
};
